import { useState } from 'react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Calculator, Clock, History, Layers, TrendingDown, AlertTriangle } from 'lucide-react';
import {
  AgingBandsTab, LowStockAlertsTab, SlowMovingTab, StockCardTab, ValuationTab,
} from '../components/InventoryAnalysisTabs';

// ─────────────────────────────────────────────────────────────────────────────
// Inventory Analysis — read-only screens over /api/accounting/inventory.
//
// Split out of the Inventory page rather than added as three more tabs to it:
// these views answer valuation and risk questions (what is this stock worth,
// how old is it, what is about to run out) on the ledger engine's own surface,
// while /inventory is the operational day-work screen over /api/inventory. The
// Inventory page keeps its own tabs untouched.
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'stock-card', label: 'Stock Card', icon: History },
  { id: 'valuation', label: 'Valuation', icon: Calculator },
  { id: 'low-stock', label: 'Low Stock', icon: AlertTriangle },
  { id: 'aging', label: 'Stock Ageing', icon: Clock },
  { id: 'slow-moving', label: 'Slow & Non-Moving', icon: TrendingDown },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function InventoryAnalysisPage() {
  const [tab, setTab] = useState<TabId>('stock-card');
  // One product selection shared by the two product-scoped reports, so moving
  // between the stock card and its valuation does not ask for the item twice.
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');

  const selectProduct = (id: string, label: string) => {
    setProductId(id);
    setProductName(label);
  };

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 p-6 pb-16">
      <PageHeader
        icon={Layers}
        eyebrow="Inventory"
        title="Inventory Analysis"
        description="Stock card, costing-method valuation and the ledger engine's own low-stock, ageing and slow-moving alerts. Read-only."
      />

      <div className="flex gap-2 border-b overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition whitespace-nowrap rounded-t-md ${
              tab === t.id
                ? 'border-primary text-primary bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <t.icon className="h-4 w-4" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {tab === 'stock-card' && <StockCardTab productId={productId} productName={productName} onSelectProduct={selectProduct} />}
      {tab === 'valuation' && <ValuationTab productId={productId} productName={productName} onSelectProduct={selectProduct} />}
      {tab === 'low-stock' && <LowStockAlertsTab />}
      {tab === 'aging' && <AgingBandsTab />}
      {tab === 'slow-moving' && <SlowMovingTab />}
    </div>
  );
}
